import { HpComponentNotFullV2, HpDiagnosticComponentV2, HpSystemGroup, HpVesaSystem, VariantGroup, VariantGroupsWithComponents } from 'api3';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, DestroyRef, ElementRef, Input, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from '@app/services/message.service';
import { BreadcrumbComponent } from '@app/components/breadcrumb/breadcrumb.component';
import { ItemBoxComponent } from '@app/components/item-box/item-box.component';
import { StepByStepDiagnosisComponent } from './step-by-step-diagnosis/step-by-step-diagnosis.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { AccordionModule } from 'primeng/accordion';
import { Button } from 'primeng/button';

export const getUniqueSystemTypeIds = (components: HpDiagnosticComponentV2[] | null | undefined): Set<number> => {
  const idSet = new Set<number>();
  components?.forEach((component) => {
    if (component.system_type_id) {
      idSet.add(component.system_type_id);
    }
  });
  return idSet;
};

export type HPDiagnosisData = {
  carTypeId: number;
  errorCode: string;
  componentIds?: number[];
  systemGroups?: {
    systemGroupId?: number;
    systemGroupName: string;
    componentData: {
      systemId: string;
      systemName: string;
      componentId: string;
      componentName: string;
    }[];
  }[];
  selectedSystemGroupName?: string;
  selectedVariantId?: HpVesaSystem['system_type_id'];
  selectedVariantGroupId?: VariantGroup['system_group_id'];
  selectedComponentId?: HpComponentNotFullV2['symbolInstanceId'];
  isExplorerMode?: boolean;
};

@Component({
  selector: 'app-hp-diagnosis',
  imports: [CommonModule, TranslocoPipe, StepByStepDiagnosisComponent, AccordionModule, ItemBoxComponent, BreadcrumbComponent, Button],
  templateUrl: './hp-diagnosis.component.html',
  host: {
    class: 'flex flex-col min-h-full p-6 relative',
  },
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HpDiagnosisComponent implements OnInit {
  // Props are injected by ModalComponent
  data = input<HPDiagnosisData | undefined>();
  @Input() close: (() => void) | undefined;

  messageService = inject(MessageService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);

  activeVariant = 0;

  serverData = signal<VariantGroupsWithComponents | undefined>(undefined);
  selectedVariantGroupId = signal<number | undefined>(undefined);
  selectedVariantId = signal<HpVesaSystem['system_type_id'] | undefined>(undefined);
  selectedComponentId = signal<HpComponentNotFullV2['symbolInstanceId'] | undefined>(undefined);
  selectedSystemGroupName = signal<string | undefined>(undefined);
  availableSystemGroups = signal<any[]>([]);
  availableVariantIds = signal<Set<number>>(new Set());
  finishedComponents = signal<number[]>([]);
  isLoading = signal(true);

  ngOnInit() {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (Object.keys(params).includes('diagnosisView')) {
        this.selectedVariantGroupId.set(params['variantGroupId'] ? Number(params['variantGroupId']) : undefined);
        this.selectedVariantId.set(params['variantId'] ? Number(params['variantId']) : undefined);
        this.selectedComponentId.set(params['componentId'] ? Number(params['componentId']) : undefined);
        this.activeVariant = params['variantId'] ? Number(params['variantId']) : 0;

        // scroll to the top of the diagnosis view
        this._scrollToTop();
      }
    });
  }

  onInputChange = effect(async () => {
    const data = this.data();
    if (!data) return;

    if (data.isExplorerMode && data.systemGroups) {
      this.availableSystemGroups.set(data.systemGroups);
      this.selectedSystemGroupName.set(data.selectedSystemGroupName);
      this.isLoading.set(false);
      return;
    }
    const carTypeId = data.carTypeId;
    const errorCode = data.errorCode;
    const componentIds = data.componentIds;
    const variantGroupId = data.selectedVariantGroupId;
    const variantId = data.selectedVariantId;

    if (!carTypeId) {
      console.warn('Car type ID is missing, cannot load diagnosis data.');
      return;
    }

    this.isLoading.set(true);

    let serverData;
    if (errorCode) {
      serverData = await this._loadVariants(carTypeId, errorCode);
    } else if (componentIds) {
      serverData = await this.messageService.getComponents(carTypeId, componentIds);
    } else {
      console.warn('[HPDiagnosisComponent] Neither errorCode nor component-IDs are given:', data);
    }

    this.serverData.set(serverData);

    this.availableVariantIds.set(getUniqueSystemTypeIds(this.serverData()?.diagnosticComponents));

    if (!variantGroupId && serverData?.variantGroups?.length) {
      const availableSystemIds = getUniqueSystemTypeIds(serverData.diagnosticComponents);
      let foundGroupId: number | undefined;

      for (const group of serverData.variantGroups) {
        // Check direct systems
        if (group.systems?.some((sys) => availableSystemIds.has(sys.system_type_id))) {
          foundGroupId = group.system_group_id;
          break;
        }

        // Check subgroups
        for (const subGroup of group.systemGroups || []) {
          if (subGroup.systems?.some((sys) => availableSystemIds.has(sys.system_type_id))) {
            if (subGroup.system_group_id !== null) {
              foundGroupId = subGroup.system_group_id;
            }
            break;
          }
        }
        if (foundGroupId) break;
      }

      // Fallback to the first group if no matching systems found
      if (!foundGroupId && serverData.variantGroups.length > 0) {
        foundGroupId = serverData.variantGroups[0].system_group_id;
      }

      this.selectedVariantGroupId.set(foundGroupId);
    }

    this.selectedVariantId.set(variantId ? Number(variantId) : undefined);
    this.isLoading.set(false);
  });

  selectedVariantGroup = computed<(VariantGroup | HpSystemGroup) | undefined>(() => {
    let variantGroup: (VariantGroup | HpSystemGroup) | undefined = undefined;
    this.serverData()?.variantGroups?.forEach((group) => {
      if (this.selectedVariantGroupId() === group.system_group_id) {
        variantGroup = group;
      }
      group.systemGroups?.forEach((systemGroup: HpSystemGroup) => {
        if (this.selectedVariantGroupId() === systemGroup.system_group_id) {
          variantGroup = systemGroup;
        }
      });
    });

    return variantGroup;
  });

  selectedVariant = computed<HpVesaSystem | undefined>(() => {
    return this.variants().find((system) => system.system_type_id === this.selectedVariantId());
  });

  variants = computed<HpVesaSystem[]>(() => {
    let variants: HpVesaSystem[] = [];
    const serverData = this.serverData();
    const selectedGroupId = this.selectedVariantGroupId();
    const availableIds = this.availableVariantIds();

    serverData?.variantGroups?.forEach((group) => {
      if (selectedGroupId === group.system_group_id) {
        if (group.systems) {
          const availableSystems = group.systems.filter((system) => availableIds.has(system.system_type_id));
          variants = variants.concat(availableSystems);
        }
      }
      group.systemGroups?.forEach((systemGroup: HpSystemGroup) => {
        if (selectedGroupId === systemGroup.system_group_id) {
          if (systemGroup.systems) {
            const availableSystems = systemGroup.systems.filter((system) => availableIds.has(system.system_type_id));
            variants = variants.concat(availableSystems);
          }
        }
      });
    });

    if (variants.length === 0 && serverData?.diagnosticComponents?.length) {
      const systemsMap = new Map<number, HpVesaSystem>();

      serverData.diagnosticComponents.forEach((comp) => {
        if (comp.system_type_id) {
          systemsMap.set(comp.system_type_id, {
            system_type_id: comp.system_type_id,
            text: comp.systemName || `Unknown System`,
          });
        }
      });

      variants = Array.from(systemsMap.values());
    }
    return variants;
  });

  components = computed<(HpComponentNotFullV2 | null)[] | undefined>(() => {
    const selectedComponents2: HpComponentNotFullV2[] = [];
    const variantGroupData = this.serverData();
    const diagnosticComponents = variantGroupData?.diagnosticComponents;
    diagnosticComponents?.forEach((component: HpDiagnosticComponentV2) => {
      if (component.system_type_id === this.selectedVariantId()) {
        if (component.component) selectedComponents2.push(component.component);
      }
    });
    return selectedComponents2;
  });

  selectedComponent = computed<HpComponentNotFullV2 | undefined>(() => {
    return (
      this.serverData()?.diagnosticComponents?.find((component) => component?.component?.symbolInstanceId === this.selectedComponentId())
        ?.component || undefined
    );
  });

  breadcrumbItems = computed(() => {
    const items = [];
    const variantGroupId = this.selectedVariantGroupId();
    const variantId = this.selectedVariantId();
    const componentId = this.selectedComponentId();

    if (this.selectedVariantGroup()) {
      items.push({
        label: this.selectedVariantGroup()?.text ?? '',
        url: this.router.routerState.snapshot.url.split('?')[0],
        queryParams: this._getUrlParams(String(variantGroupId ?? ''), undefined, undefined),
      });
    }

    if (this.selectedVariant()) {
      items.push({
        label: this.selectedVariant()?.text ?? '',
        url: this.router.routerState.snapshot.url.split('?')[0],
        queryParams: this._getUrlParams(String(this.selectedVariantGroupId()), String(variantId), undefined),
      });
    }

    if (this.selectedComponent()) {
      const componentNumber = this.selectedComponent()?.number ?? '';
      const componentName = `${componentNumber} ${this.selectedComponent()?.name ?? ''}`.trim();
      items.push({
        label: componentName,
        url: this.router.routerState.snapshot.url.split('?')[0],
        queryParams: this._getUrlParams(String(this.selectedVariantGroupId()), String(variantId), String(componentId)),
      });
    }

    return items;
  });

  onBackClick() {
    if (this.selectedComponentId()) {
      this.selectedComponentId.set(undefined);
    } else if (this.selectedVariantId()) {
      this.selectedVariantId.set(undefined);
    } else if (this.selectedVariantGroupId()) {
      this.selectedVariantGroupId.set(undefined);
    } else if (this.data()?.isExplorerMode && this.selectedSystemGroupName()) {
      this.selectedSystemGroupName.set(undefined);
      this.serverData.set(undefined);
    } else {
      this.close?.();
    }
  }

  onSystemGroupSelect(systemGroupName: string) {
    const systemGroup = this.availableSystemGroups().find((sg) => sg.systemGroupName === systemGroupName);
    if (!systemGroup) return;

    this.selectedSystemGroupName.set(systemGroupName);

    const componentIds = systemGroup.componentData.map((comp: any) => Number(comp.componentId));

    const carTypeId = this.data()?.carTypeId;
    if (!carTypeId) return;
    this.messageService.getComponents(carTypeId, componentIds).then((serverData) => {
      this.serverData.set(serverData);
      this.availableVariantIds.set(getUniqueSystemTypeIds(serverData?.diagnosticComponents));

      if (serverData?.variantGroups?.length) {
        this.selectedVariantGroupId.set(serverData.variantGroups[0].system_group_id);
      }
      this.isLoading.set(false);
    });
  }

  onVariantChange(variantId: HpVesaSystem['system_type_id']) {
    this.selectedVariantId.set(variantId);
    this._updateUrlParams();
    this._scrollToTop();
  }

  onDiagnosisFinish() {
    const finishedComponentId = this.selectedComponentId();
    this.finishedComponents.update((ids) => (finishedComponentId ? [...ids, finishedComponentId] : ids));
    this.selectedComponentId.set(undefined);
    this._updateUrlParams();
  }

  clearSelectedVariant() {
    this.selectedVariantId.set(undefined);
    this._updateUrlParams();
    this._scrollToTop();
  }

  async openDiagnosis(component: HpComponentNotFullV2 | null) {
    if (!component?.symbolInstanceId || !component?.systemTypeId) return;

    const carTypeId = this.data()?.carTypeId;
    if (!carTypeId) return;

    this.selectedComponentId.set(component.symbolInstanceId);
    this._updateUrlParams();
    this._scrollToTop();
  }

  private async _loadVariants(carTypeId: number | undefined, errorCode: string | undefined): Promise<VariantGroupsWithComponents | undefined> {
    if (!carTypeId || !errorCode) {
      return undefined;
    }
    return await this.messageService.getVariantGroupsWithComponents(carTypeId, errorCode);
  }

  private _updateUrlParams() {
    this.router.navigate([], {
      relativeTo: this.router.routerState.root,
      queryParams: {
        variantId: this.selectedVariantId(),
        variantGroupId: this.selectedVariantGroupId(),
        componentId: this.selectedComponentId(),
      },
      queryParamsHandling: 'merge',
    });
  }

  private _getUrlParams(selectedVariantGroupId: string, selectedVariantId: string | undefined, selectedComponentId: string | undefined) {
    const currentUrl = this.router.routerState.snapshot.url;
    const url = new URL(currentUrl, window.location.origin);

    if (selectedVariantGroupId) {
      url.searchParams.set('variantGroupId', selectedVariantGroupId);
    } else {
      url.searchParams.delete('variantGroupId');
    }

    if (selectedVariantId) {
      url.searchParams.set('variantId', selectedVariantId);
    } else {
      url.searchParams.delete('variantId');
    }

    if (selectedComponentId) {
      url.searchParams.set('componentId', selectedComponentId);
    } else {
      url.searchParams.delete('componentId');
    }

    const jsonParams: { [key: string]: any } = {};
    url.searchParams.forEach((value, key) => {
      jsonParams[key] = value;
    });

    return jsonParams;
  }

  private _scrollToTop() {
    if (this.elementRef?.nativeElement) {
      this.elementRef.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }
}
